// src/hooks/assistant/useWorkspaceMode.ts
// Hook for handling workspace mode (widget generation) in the assistant

import { useState, useCallback, useMemo } from "react";
import {
  getDashboardAIService,
  thesisToContext,
  type AIWidgetSpec,
} from "@/services/operationCenter/DashboardAIService";
import { useActiveDashboard } from "@/stores/dashboardStore";
import { useThesisByDashboard } from "@/stores/thesisStore";
import type { ChatMessage } from "@/services/assistant/AssistantService";

export interface WorkspaceState {
  messages: ChatMessage[];
  isLoading: boolean;
  /** Widgets generated from the last response */
  pendingWidgets: AIWidgetSpec[];
}

export interface WorkspaceActions {
  sendMessage: (content: string) => Promise<void>;
  clearPendingWidgets: () => void;
  reset: () => void;
}

export function useWorkspaceMode(): [WorkspaceState, WorkspaceActions] {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingWidgets, setPendingWidgets] = useState<AIWidgetSpec[]>([]);

  // Get current dashboard's thesis for context
  const activeDashboard = useActiveDashboard();
  const thesis = useThesisByDashboard(activeDashboard?.id);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) return;

      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: content.trim(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setPendingWidgets([]);

      try {
        const service = getDashboardAIService();

        const isDashboardRequest =
          content.toLowerCase().includes("dashboard") ||
          content.toLowerCase().includes("build me") ||
          content.toLowerCase().includes("create me");

        // Convert thesis to context for AI generation
        const thesisContext = thesis ? thesisToContext(thesis) : undefined;

        let widgets: AIWidgetSpec[] = [];
        let responseContent: string;

        if (isDashboardRequest) {
          const dashboardSpec = await service.generateDashboard(
            content,
            thesisContext,
          );
          widgets = dashboardSpec.widgets;
          responseContent =
            widgets.length > 0
              ? `I've created ${widgets.length} widget${widgets.length !== 1 ? "s" : ""} for your dashboard. Click "Add" to place them on your workspace.`
              : "I couldn't create widgets from that request. Try being more specific about what you want to see.";
        } else {
          widgets = await service.generateWidgets(content);
          if (widgets.length === 0) {
            responseContent =
              "I couldn't create a widget from that. Try being more specific, like 'Add a BTC price card' or 'Create a chart comparing ETH and SOL'.";
          } else {
            responseContent =
              widgets.length === 1
                ? 'Here\'s the widget I created. Click "Add" to place it on your workspace.'
                : `I've created ${widgets.length} widgets. Click "Add" to place them on your workspace.`;
          }
        }

        const assistantMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: responseContent,
        };

        setMessages((prev) => [...prev, assistantMessage]);
        setPendingWidgets(widgets);
      } catch (err) {
        const errorMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `Error: ${err instanceof Error ? err.message : "Unknown error occurred"}`,
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, thesis],
  );

  const clearPendingWidgets = useCallback(() => {
    setPendingWidgets([]);
  }, []);

  const reset = useCallback(() => {
    setMessages([]);
    setPendingWidgets([]);
    setIsLoading(false);
  }, []);

  const state: WorkspaceState = useMemo(
    () => ({
      messages,
      isLoading,
      pendingWidgets,
    }),
    [messages, isLoading, pendingWidgets],
  );

  const actions: WorkspaceActions = useMemo(
    () => ({
      sendMessage,
      clearPendingWidgets,
      reset,
    }),
    [sendMessage, clearPendingWidgets, reset],
  );

  return [state, actions] as const;
}

export default useWorkspaceMode;
